"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type SearchResult = {
  id: string;
  title: string;
  url: string;
  snippet?: string;
  trustScore?: number;
  isOfficial?: boolean;
};

type DocumentChunk = {
  id: string;
  type: "text" | "image" | "code" | "heading" | "list";
  content: unknown;
  language?: string;
};

export default function Context7() {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  const [chunks, setChunks] = useState<DocumentChunk[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const canSearch = query.trim().length > 0 && !isSearching;

  const runSearch = async () => {
    if (!canSearch) return;
    setIsSearching(true);
    setResults([]);
    setSearchError(null);
    setActiveUrl(null);
    setChunks(null);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const res = await fetch(
        `/api/context7/search?q=${encodeURIComponent(query)}`,
        {
          method: "GET",
          signal: ac.signal,
        }
      );
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        setSearchError((data && data.error) || `Search failed: ${res.status}`);
        setResults([]);
        return;
      }
      const data = (await res.json()) as {
        results?: SearchResult[];
        error?: string;
        details?: string;
      };
      if (data?.results && data.results.length > 0) {
        setResults(data.results);
        // Auto-load top result (use current query as topic hint)
        const top = data.results[0];
        if (top?.id?.startsWith("/")) {
          loadLibraryDocs(top.id);
        } else if (top?.url) {
          loadUrl(top.url);
        }
      } else {
        setResults([]);
        if (data?.error)
          setSearchError(
            `${data.error}${data.details ? `: ${data.details}` : ""}`
          );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const loadUrl = async (url: string) => {
    setActiveUrl(url);
    setChunks(null);
    try {
      const res = await fetch(
        `/api/context7/fetch?url=${encodeURIComponent(url)}`
      );
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
          details?: string;
        } | null;
        const errorMsg = data?.error
          ? `${data.error}${data.details ? `: ${data.details}` : ""}`
          : `Fetch failed: ${res.status}`;
        setSearchError(errorMsg);
        setChunks([]);
        return;
      }
      const data = (await res.json()) as { chunks: DocumentChunk[] };
      setChunks(data.chunks || []);
    } catch (err) {
      console.error(err);
      setSearchError(err instanceof Error ? err.message : "Failed to load URL");
      setChunks([]);
    }
  };

  const loadLibraryDocs = async (libraryId: string) => {
    const canonicalUrl = `https://context7.com${libraryId}`;
    setActiveUrl(canonicalUrl);
    setChunks(null);
    try {
      const res = await fetch(
        `/api/context7/docs?id=${encodeURIComponent(libraryId)}&tokens=4000`
      );
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
          details?: string;
        } | null;
        const errorMsg = data?.error
          ? `${data.error}${data.details ? `: ${data.details}` : ""}`
          : `Docs failed: ${res.status}`;
        setSearchError(errorMsg);
        setChunks([]);
        return;
      }
      const data = (await res.json()) as { chunks: DocumentChunk[] };
      setChunks(data.chunks || []);
    } catch (err) {
      console.error(err);
      setSearchError(
        err instanceof Error ? err.message : "Failed to load library docs"
      );
      setChunks([]);
    }
  };

  function toPlainText(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return value.map(toPlainText).join("\n");
    if (typeof value === "object") {
      const v = value as Record<string, unknown>;
      if (typeof v.text === "string") return v.text as string;
      if (typeof v.content === "string") return v.content as string;
      // Best-effort stringify small objects
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  // Structured CODE SNIPPETS support
  type SnippetCode = { language?: string; code: string };
  type Snippet = {
    id: string;
    title: string;
    description: string;
    source: string;
    codes: SnippetCode[];
  };

  function parseCodeSnippets(text: string): Snippet[] {
    const headerIdx = text.indexOf("CODE SNIPPETS");
    if (headerIdx === -1) return [];
    const after = text.slice(headerIdx);
    // Split by separator lines of dashes used in Context7 docs blocks
    const parts = after.split(/\n-{10,}\n/g).slice(1);
    const snippets: Snippet[] = [];
    for (let i = 0; i < parts.length; i++) {
      const block = parts[i];
      const titleMatch = /TITLE:\s*(.+)\n/.exec(block);
      const descMatch =
        /DESCRIPTION:\s*([\s\S]*?)\n\n/.exec(block) ||
        /DESCRIPTION:\s*(.+)\n/.exec(block);
      const sourceMatch = /SOURCE:\s*(\S+)/.exec(block);
      const codes: SnippetCode[] = [];
      let m: RegExpExecArray | null;
      // LANGUAGE + CODE fenced
      const langCodeRe =
        /LANGUAGE:\s*([^\n]+)[\s\S]*?CODE:\s*```(?:([a-zA-Z0-9_-]+)\n)?([\s\S]*?)```/g;
      while ((m = langCodeRe.exec(block))) {
        const langLabel = (m[1] || m[2] || "").trim();
        const code = (m[3] || "").replace(/\n$/, "");
        codes.push({ language: langLabel || undefined, code });
      }
      // Fallback: CODE fenced blocks without a preceding LANGUAGE line
      if (codes.length === 0) {
        const codeOnlyRe = /CODE:\s*```(?:([a-zA-Z0-9_-]+)\n)?([\s\S]*?)```/g;
        while ((m = codeOnlyRe.exec(block))) {
          const lang = (m[1] || "").trim() || undefined;
          const code = (m[2] || "").replace(/\n$/, "");
          codes.push({ language: lang, code });
        }
      }
      const title = titleMatch?.[1]?.trim() || "Untitled";
      const description = descMatch?.[1]?.trim() || "";
      const source = sourceMatch?.[1]?.trim() || "";
      if (title || description || codes.length > 0) {
        snippets.push({ id: `${i}`, title, description, source, codes });
      }
    }
    return snippets;
  }

  function SnippetCard({ snippet }: { snippet: Snippet }) {
    const [active, setActive] = useState(0);
    const activeCode = snippet.codes[active] || snippet.codes[0];
    return (
      <div className="border border-border rounded-md">
        <div className="p-2 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold leading-6">
              {snippet.title}
            </h3>
            {snippet.source && (
              <a
                href={snippet.source}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs shrink-0 underline decoration-dotted text-accent-foreground"
              >
                Source
              </a>
            )}
          </div>
          {snippet.description && (
            <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">
              {snippet.description}
            </p>
          )}
        </div>
        {snippet.codes.length > 0 && (
          <div className="p-2">
            {snippet.codes.length > 1 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {snippet.codes.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`h-7 px-2 rounded text-xs ${
                      active === i
                        ? "border border-accent text-foreground"
                        : "border border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {c.language || `Code ${i + 1}`}
                  </button>
                ))}
              </div>
            )}
            <div className="relative">
              {activeCode?.language && (
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {activeCode.language}
                </span>
              )}
              <pre className="rounded p-2 overflow-auto text-sm border border-border">
                <code
                  className={
                    activeCode?.language
                      ? `language-${activeCode.language}`
                      : undefined
                  }
                >
                  {activeCode?.code}
                </code>
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  const content = useMemo(() => {
    if (!chunks) return null;
    return chunks.map((chunk) => {
      const text = toPlainText(chunk.content);
      switch (chunk.type) {
        case "heading":
          return (
            <h3 key={chunk.id} className="text-lg font-semibold mt-4">
              {text}
            </h3>
          );
        case "code":
          return (
            <pre
              key={chunk.id}
              className="rounded p-2 overflow-auto text-sm border border-border"
            >
              <code>{text}</code>
            </pre>
          );
        case "image": {
          const sourceObj =
            chunk.content && typeof chunk.content === "object"
              ? (chunk.content as { src?: unknown; url?: unknown })
              : null;
          const candidate = sourceObj?.src ?? sourceObj?.url;
          const srcString: string | undefined =
            typeof candidate === "string"
              ? candidate
              : typeof chunk.content === "string"
              ? chunk.content
              : undefined;
          if (!srcString) return null;
          return (
            <img
              key={chunk.id}
              src={srcString}
              alt="Context7 image"
              className="max-w-full rounded border border-border"
            />
          );
        }
        case "list":
          return (
            <ul key={chunk.id} className="list-disc pl-6 space-y-1">
              {text.split("\n").map((li, i) => (
                <li key={i}>{li}</li>
              ))}
            </ul>
          );
        default: {
          // Try to parse structured code snippets. If present, render cards.
          const snippets = parseCodeSnippets(text);
          if (snippets.length > 0) {
            return (
              <div key={chunk.id} className="space-y-3">
                {snippets.map((s) => (
                  <SnippetCard key={s.id} snippet={s} />
                ))}
              </div>
            );
          }
          return (
            <div key={chunk.id} className="space-y-3">
              <p className="leading-7 whitespace-pre-wrap">{text}</p>
            </div>
          );
        }
      }
    });
  }, [chunks]);

  return (
    <div className="w-full h-full flex flex-col bg-background text-foreground">
      <div className="p-3 border-b border-border flex items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") runSearch();
          }}
          placeholder="Search the web with Context7..."
          className="flex-1 h-9 px-3 rounded border border-border bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <button
          onClick={runSearch}
          disabled={!canSearch}
          className="h-9 px-4 rounded bg-accent text-accent-foreground disabled:opacity-50"
        >
          {isSearching ? "Searching..." : "Search"}
        </button>
      </div>
      <div className="flex-1 grid grid-cols-12 gap-0 min-h-0">
        <aside className="col-span-4 border-r border-border overflow-auto">
          {results.length === 0 ? (
            <div className="p-4 text-muted-foreground text-sm">
              {searchError ? (
                <div className="text-red-500">{searchError}</div>
              ) : (
                "No results yet."
              )}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {results.map((r) => (
                <li
                  key={r.id}
                  className={`p-3 cursor-pointer hover:bg-surface ${
                    activeUrl === r.url ? "bg-surface" : ""
                  }`}
                  onClick={() => {
                    if (r.id?.startsWith("/")) {
                      loadLibraryDocs(r.id);
                    } else {
                      loadUrl(r.url);
                    }
                  }}
                >
                  <div className="font-medium truncate" title={r.title}>
                    {r.title || r.url}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    {typeof r.trustScore === "number" && (
                      <span>Trust: {r.trustScore.toFixed(1)}</span>
                    )}
                    {r.isOfficial && (
                      <span className="px-1 rounded bg-accent/20 text-accent-foreground border border-accent/40">
                        Official
                      </span>
                    )}
                  </div>
                  {r.snippet && (
                    <div className="text-xs text-muted-foreground line-clamp-2">
                      {r.snippet}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.url}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
        <main className="col-span-8 overflow-auto p-4 space-y-3">
          {!activeUrl ? (
            <div className="text-sm text-muted-foreground">
              Select a result to load content.
            </div>
          ) : chunks == null ? (
            <div className="text-sm text-muted-foreground">
              Loading content...
            </div>
          ) : chunks.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No content extracted.
            </div>
          ) : (
            content
          )}
        </main>
      </div>
    </div>
  );
}
